import React, { useEffect, useState, useMemo } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { apiService } from '../services/apiService';
import { portalService } from '../services/portalService';

const ChildrenAssignments = () => {
  const [children, setChildren]         = useState([]);
  const [selectedId, setSelectedId]     = useState(null);
  const [assessments, setAssessments]   = useState([]);
  const [grades, setGrades]             = useState([]);
  const [tasks, setTasks]               = useState([]);
  const [submissions, setSubmissions]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [dataLoading, setDataLoading]   = useState(false);
  const [error, setError]               = useState('');
  const [tab, setTab]                   = useState('assignments');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const dashboard = await portalService.getParentDashboard();
        const kids = dashboard.children || [];
        setChildren(kids);
        if (kids.length) setSelectedId(kids[0].id);
      } catch { setError('Failed to load children.'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    const load = async () => {
      try {
        setDataLoading(true);
        const [aData, gData, tData, sData] = await Promise.all([
          apiService.get(`assessments/student/${selectedId}`),
          apiService.get(`grades/student/${selectedId}`),
          apiService.getAll('assignments'),
          apiService.get('assignments/my-submissions').catch(() => []),
        ]);
        setAssessments(aData || []);
        setGrades(gData || []);
        setTasks(tData || []);
        setSubmissions(Array.isArray(sData) ? sData : []);
      } catch { setError('Failed to load data.'); }
      finally { setDataLoading(false); }
    };
    load();
  }, [selectedId]);

  const gradedMap  = useMemo(() => grades.reduce((acc, g) => { acc[g.assessmentId] = g; return acc; }, {}), [grades]);
  const subMap     = useMemo(() => submissions.reduce((acc, s) => { acc[s.taskId] = s; return acc; }, {}), [submissions]);
  const upcoming   = assessments.filter(a => !gradedMap[a._id]);
  const graded     = assessments.filter(a =>  gradedMap[a._id]);
  const child      = children.find(c => c.id === selectedId);

  const statusBadge = status => {
    const colors = { submitted: '#1976D2', late: '#F57C00', graded: '#388E3C', returned: '#7B1FA2' };
    const c = colors[status] || '#666';
    return <span style={{ background: `${c}22`, color: c, padding: '0.15rem 0.5rem', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600 }}>{status}</span>;
  };

  if (loading) return <LoadingSpinner message="Loading..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Assignments & Assessments {child ? `— ${child.name}` : ''}</h1>
      </div>
      {error && <div className="error-message">{error}</div>}

      {children.length > 1 && (
        <div className="form-group" style={{ maxWidth: 300, marginBottom: '1.5rem' }}>
          <label>Select Child</label>
          <select value={selectedId} onChange={e => setSelectedId(Number(e.target.value))}>
            {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      <div className="messages-tabs" style={{ marginBottom: '1.5rem' }}>
        <button className={`tab-button ${tab === 'assignments' ? 'active' : ''}`} onClick={() => setTab('assignments')}>
          Assignments ({tasks.length})
        </button>
        <button className={`tab-button ${tab === 'assessments' ? 'active' : ''}`} onClick={() => setTab('assessments')}>
          Assessments ({assessments.length})
        </button>
      </div>

      {dataLoading ? <LoadingSpinner message="Loading..." /> : (
        <>
          {/* ASSIGNMENTS TAB — shows submission status */}
          {tab === 'assignments' && (
            tasks.length === 0 ? (
              <div className="no-data-message">No assignments found for this student's class.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {tasks.map(task => {
                  const sub = subMap[task.id];
                  const isOverdue = new Date(task.dueDate) < new Date();
                  return (
                    <div key={task.id} style={{ background: 'var(--card-bg)', border: `1px solid ${!sub && isOverdue ? '#c62828' : 'var(--border-color)'}`, borderRadius: 8, padding: '1.1rem 1.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                            <strong>{task.title}</strong>
                            <span style={{ fontSize: '0.78rem', background: '#e3f2fd', color: '#1565c0', padding: '0.1rem 0.4rem', borderRadius: 4 }}>{task.type}</span>
                          </div>
                          <div style={{ fontSize: '0.82rem', color: 'var(--text-light)' }}>
                            {task.className}{(task.moduleName || task.subjectName) && ` · ${task.moduleName || task.subjectName}`} · Max: {task.maxMarks} marks
                          </div>
                          <div style={{ fontSize: '0.82rem', color: isOverdue && !sub ? '#c62828' : 'var(--text-light)', marginTop: '0.2rem' }}>
                            Due: {new Date(task.dueDate).toLocaleString()} {isOverdue && !sub ? '⚠ Overdue' : ''}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.3rem' }}>
                          {sub ? (
                            <>
                              {statusBadge(sub.status)}
                              {sub.marksObtained !== null && sub.marksObtained !== undefined && (
                                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{sub.marksObtained} / {task.maxMarks}</span>
                              )}
                              <span style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>
                                Submitted: {new Date(sub.submittedAt).toLocaleDateString()}
                              </span>
                            </>
                          ) : (
                            <span style={{ background: '#f8d7da', color: '#721c24', padding: '0.15rem 0.6rem', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600 }}>
                              Not Submitted
                            </span>
                          )}
                        </div>
                      </div>
                      {sub?.feedback && (
                        <div style={{ marginTop: '0.6rem', background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, padding: '0.6rem 0.75rem', fontSize: '0.85rem' }}>
                          <strong>Feedback:</strong> {sub.feedback}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}

          {/* ASSESSMENTS TAB */}
          {tab === 'assessments' && (
            <>
              <div className="dashboard-section" style={{ marginBottom: '1.5rem' }}>
                <h2>Upcoming ({upcoming.length})</h2>
                <div className="table-responsive">
                  <table className="data-table">
                    <thead><tr><th>Assessment</th><th>Module</th><th>Type</th><th>Date</th><th>Total Marks</th></tr></thead>
                    <tbody>
                      {upcoming.length === 0 ? (
                        <tr><td colSpan={5} className="no-data-message">No upcoming assessments.</td></tr>
                      ) : upcoming.map(a => (
                        <tr key={a._id}>
                          <td><strong>{a.title}</strong></td>
                          <td>{a.moduleName || a.subjectName}</td>
                          <td>{a.type}</td>
                          <td>{new Date(a.date).toLocaleDateString()}</td>
                          <td>{a.totalMarks}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="dashboard-section">
                <h2>Graded ({graded.length})</h2>
                <div className="table-responsive">
                  <table className="data-table">
                    <thead><tr><th>Assessment</th><th>Module</th><th>Marks</th><th>%</th><th>Remarks</th></tr></thead>
                    <tbody>
                      {graded.length === 0 ? (
                        <tr><td colSpan={5} className="no-data-message">No graded assessments yet.</td></tr>
                      ) : graded.map(a => {
                        const g = gradedMap[a._id];
                        return (
                          <tr key={a._id}>
                            <td><strong>{a.title}</strong></td>
                            <td>{a.moduleName || a.subjectName}</td>
                            <td>{g.marksObtained} / {g.totalMarks}</td>
                            <td>{g.percentage ?? 0}%</td>
                            <td>{g.remarks || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ChildrenAssignments;
